/* eslint-disable prettier/prettier */
import { useEffect, useState, useRef, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import { Col, Row, Button, Spinner, Form } from 'react-bootstrap'
import { TextWithHint } from '../../../components/atoms/TextWithHint'
import { EntityDetails } from '../../../components/organisms/EntityDetails'
import { DetailsSection } from '../../../components/templates/DetailsSection'
import { PaymentsTable } from './PaymentsTable'
import { PersonsTable } from './PersonsTable'
import { getContract } from '../../../domain/usecases/Contracts'
import {
  ContractStatuses,
  ContractWithPaymentPart
} from '../../../domain/models/ContractsModel'
import { convertNumberToCurrencyFormat } from '../../../utils/convertNumberToCurrencyFormat'
import {
  uploadContractFile,
  syncContractToUau,
  getUauSyncStatus,
  checkUauAvailability,
  updateBillingPerson,
  UauSyncLog,
  UauAvailability
} from '../../../services/contracts'
import { AppContext } from '../../../app'

import {
  serializedValuesForPaymentsTable,
  serializedValuesForPersonsTable
} from '../../../domain/converters/Contracts'
import { CancelContractModal } from 'components/molecules/CancelContractModal'
import { CancelContract } from 'data/usecases/Contracts/RemoteCancelContract'
import { IconButton } from 'components/atoms/IconButton'
import { DetailButtonsRow } from 'components/molecules/DetailButtonsRow'
import { usePermissions } from 'hooks/usePermissions'
import { GenerateItauBoletosModal } from './GenerateItauBoletosModal'
import { SyncUauInstallmentsModal } from './SyncUauInstallmentsModal'

export type IParams = {
  id: string
}

export type StatusBadge = 'success' | 'info' | 'warning' | 'error'

export type ContractDetailsProps = {
  remoteGetContract: getContract
  remoteResendSignatureEmail: (signatureId: string) => Promise<any>
  /** "Excluir" no produto é cancelar (POST /contracts/:id/cancel, permissão contracts.delete). */
  remoteCancelContract?: CancelContract
}

export const ContractDetails = ({
  remoteGetContract,
  remoteResendSignatureEmail,
  remoteCancelContract
}: ContractDetailsProps) => {
  const [values, setValues] = useState<ContractWithPaymentPart>()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [showItauModal, setShowItauModal] = useState(false)
  const [showSyncInstallmentsModal, setShowSyncInstallmentsModal] =
    useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSyncingUau, setIsSyncingUau] = useState(false)
  const [uauSyncStatus, setUauSyncStatus] = useState<UauSyncLog | null>(null)
  const [uauAvailability, setUauAvailability] =
    useState<UauAvailability | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const params = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const { can } = usePermissions()
  const { user } = useContext(AppContext)

  const { id } = params as IParams

  const handleResendEmail = async (signatureId: string) => {
    await remoteResendSignatureEmail(signatureId)
  }

  const fetchUauData = async () => {
    if (!user?.token) return

    try {
      // First check if UAU is available for this contract
      const availability = await checkUauAvailability(
        id,
        user.token,
        user.selectedContextEncoded
      )
      setUauAvailability(availability)

      // Only fetch sync status if UAU is enabled
      if (availability.enabled) {
        const status = await getUauSyncStatus(
          id,
          user.token,
          user.selectedContextEncoded
        )
        setUauSyncStatus(status)

        // If sync is in progress, poll again in 3 seconds
        if (status?.status === 'pending' || status?.status === 'in_progress') {
          setTimeout(fetchUauData, 3000)
        }
      }
    } catch (error) {
      console.log('Could not fetch UAU data:', error)
    }
  }

  useEffect(() => {
    const getOneContract = async () => {
      try {
        dispatch({ type: 'SET_LOADING', loading: true })

        const contract = await remoteGetContract.getOne({ id })
        setValues(contract)
      } catch (error) {
        addToast('Contrato não encontrado.', {
          appearance: 'error',
          autoDismiss: true
        })
        history.push('/contracts?layout=grid')
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false })
      }
    }

    getOneContract()
    fetchUauData()
  }, [])

  const handleCloseDeleteModal = () => setShowDeleteModal(false)
  const handleOpenDeleteModal = () => setShowDeleteModal(true)

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      addToast('Apenas arquivos PDF são permitidos.', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    try {
      setIsUploading(true)
      const result = await uploadContractFile(
        id,
        file,
        user?.token || '',
        user?.selectedContextEncoded
      )

      if (result.success) {
        addToast('Arquivo do contrato enviado com sucesso!', {
          appearance: 'success',
          autoDismiss: true
        })
        // Update local state with new URL
        if (values) {
          setValues({
            ...values,
            contract: {
              ...values.contract,
              url: result.url
            }
          })
        }
      }
    } catch (error: any) {
      addToast(error.response?.data?.message || 'Erro ao enviar arquivo.', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Antes só mostrava "O Contrato foi excluída com sucesso!" sem chamar o backend: o
  // contrato continuava ativo. Agora cancela, como o "Cancelar" da listagem.
  const handleDeleteContract = async (): Promise<void> => {
    setCancelLoading(true)
    try {
      if (!remoteCancelContract) throw new Error('cancelamento indisponível')
      await remoteCancelContract.cancel(id)
      addToast('Contrato cancelado com sucesso!', {
        appearance: 'success',
        autoDismiss: true
      })
      setValues(await remoteGetContract.getOne({ id }))
    } catch (error) {
      addToast('Não foi possível cancelar o contrato.', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setCancelLoading(false)
      handleCloseDeleteModal()
    }
  }

  const handleSyncToUau = async () => {
    try {
      setIsSyncingUau(true)
      await syncContractToUau(
        id,
        user?.token || '',
        user?.selectedContextEncoded
      )
      addToast(
        'Sincronização iniciada! Você pode sair da página. O status será atualizado automaticamente.',
        {
          appearance: 'info',
          autoDismiss: true
        }
      )
      // Start polling for status
      setTimeout(fetchUauData, 2000)
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Erro ao iniciar sincronização com UAU'
      addToast(errorMessage, {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsSyncingUau(false)
    }
  }

  const renderUauIntegrationSection = () => {
    // Only show if user has permission AND UAU is enabled for this company
    if (!can.update('contracts')) return null
    if (!uauAvailability?.enabled) return null

    const getStatusInfo = () => {
      if (!uauSyncStatus) {
        return {
          color: '#6c757d',
          bgColor: '#f8f9fa',
          text: 'Não sincronizado',
          icon: 'icon-cloud-off'
        }
      }

      const statusConfig = {
        pending: {
          color: '#856404',
          bgColor: '#fff3cd',
          text: 'Aguardando...',
          icon: 'icon-clock'
        },
        in_progress: {
          color: '#004085',
          bgColor: '#cce5ff',
          text: 'Sincronizando...',
          icon: 'icon-refresh-cw'
        },
        success: {
          color: '#155724',
          bgColor: '#d4edda',
          text: `Sincronizado com UAU (Venda #${uauSyncStatus.uau_venda_numero})`,
          icon: 'icon-check-circle'
        },
        error: {
          color: '#721c24',
          bgColor: '#f8d7da',
          text: 'Erro na sincronização',
          icon: 'icon-alert-circle'
        }
      }

      return statusConfig[uauSyncStatus.status]
    }

    const statusInfo = getStatusInfo()
    const isProcessing =
      isSyncingUau ||
      uauSyncStatus?.status === 'pending' ||
      uauSyncStatus?.status === 'in_progress'
    const isSynced = uauSyncStatus?.status === 'success'
    const isConfigured = uauAvailability?.configured !== false

    // Show warning if UAU is enabled but not properly configured
    if (!isConfigured) {
      return (
        <DetailsSection
          title="Integração UAU"
          content={
            <Row className="pt-2 align-items-center">
              <Col xs="auto">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <i
                    className="feather icon-alert-triangle"
                    style={{ marginRight: '8px' }}
                  />
                  Configuração incompleta
                </div>
              </Col>
              {uauAvailability?.missingConfig && (
                <Col xs="auto">
                  <span style={{ color: '#856404', fontSize: '13px' }}>
                    Faltando: {uauAvailability.missingConfig.join(', ')}
                  </span>
                </Col>
              )}
            </Row>
          }
        />
      )
    }

    return (
      <DetailsSection
        title="Integração UAU"
        content={
          <Row className="pt-2 align-items-center">
            <Col xs="auto">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: statusInfo.bgColor,
                  color: statusInfo.color,
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <i
                  className={`feather ${statusInfo.icon}`}
                  style={{
                    marginRight: '8px',
                    animation: isProcessing ? 'spin 1s linear infinite' : 'none'
                  }}
                />
                {statusInfo.text}
              </div>
            </Col>
            {uauSyncStatus?.status === 'error' &&
              uauSyncStatus.error_message && (
                <Col xs="auto">
                  <span style={{ color: '#721c24', fontSize: '13px' }}>
                    {uauSyncStatus.error_message}
                  </span>
                </Col>
              )}
            <Col xs="auto">
              {!isSynced && (
                <Button
                  variant={
                    uauSyncStatus?.status === 'error'
                      ? 'outline-danger'
                      : 'outline-primary'
                  }
                  size="sm"
                  onClick={handleSyncToUau}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Sincronizando...
                    </>
                  ) : uauSyncStatus?.status === 'error' ? (
                    <>
                      <i className="feather icon-refresh-cw me-1" />
                      Tentar novamente
                    </>
                  ) : (
                    <>
                      <i className="feather icon-upload-cloud me-1" />
                      Sincronizar com UAU
                    </>
                  )}
                </Button>
              )}
            </Col>
          </Row>
        }
      />
    )
  }

  return (
    <EntityDetails
      title="Contrato de Compra e Venda"
      sections={
        <>
          <CancelContractModal
            show={showDeleteModal}
            onHide={handleCloseDeleteModal}
            onConfirm={handleDeleteContract}
            loading={cancelLoading}
          />
          <DetailsSection
            title=""
            content={
              <div>
                <Row>
                  <Col>
                    <TextWithHint
                      text={values?.contract?.project?.name}
                      hint="Razão Social"
                      isTitle
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextWithHint
                      text={
                        values?.contract.url ? (
                          <a
                            href={values?.contract.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Abrir
                          </a>
                        ) : (
                          <>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              accept="application/pdf"
                              style={{ display: 'none' }}
                            />
                            {can.update('contracts') ? (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleUploadClick}
                                disabled={isUploading}
                              >
                                {isUploading ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                    />
                                    Enviando...
                                  </>
                                ) : (
                                  <>
                                    <i className="feather icon-upload me-1" />
                                    Enviar PDF
                                  </>
                                )}
                              </Button>
                            ) : (
                              <span style={{ color: '#999' }}>
                                Não disponível
                              </span>
                            )}
                          </>
                        )
                      }
                      hint="Documento do Contrato"
                    />

                    <TextWithHint
                      text={values?.contract?.project?.ri}
                      hint="Registro da Incorporação"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={3}>
                    <TextWithHint
                      text={values?.contract?.project.address.address}
                      hint="Logradouro"
                    />
                    <TextWithHint
                      text={values?.contract?.project.address.zip_code}
                      hint="CEP"
                    />
                  </Col>
                  <Col>
                    <TextWithHint
                      text={values?.contract?.project.address.number}
                      hint="Número"
                    />
                    <TextWithHint
                      text={values?.contract?.project.address.district}
                      hint="Bairro"
                    />
                  </Col>
                  <Col>
                    <TextWithHint
                      text={values?.contract?.project?.address?.city}
                      hint="Cidade"
                    />
                    <TextWithHint
                      text={values?.contract?.project?.address?.state}
                      hint="Estado"
                    />
                  </Col>
                  <Col xs={3}>
                    <TextWithHint
                      text={values?.contract?.unit.name}
                      hint="Unidade Autônoma"
                    />
                  </Col>
                  <Col>
                    <TextWithHint
                      text={values?.contract?.unit.building.name}
                      hint="Bloco"
                    />
                  </Col>
                </Row>
              </div>
            }
          />
          <DetailsSection
            title="Dados do Pagamento"
            content={
              <Row className="pt-2">
                <Col xs={2}>
                  <TextWithHint
                    text={convertNumberToCurrencyFormat(
                      Number(values?.contract.unit.value)
                    )}
                    hint="Valor do Imóvel"
                  />
                </Col>
                <Col xs={2}>
                  <TextWithHint
                    text={convertNumberToCurrencyFormat(
                      Number(values?.contract.value)
                    )}
                    hint="Valor Negociado"
                  />
                </Col>
              </Row>
            }
          />
          <DetailsSection
            title="Dados da SPE"
            content={
              <Row className="pt-2">
                <Col xs={2}>
                  <TextWithHint text={values?.contract?.spe.cnpj} hint="CNPJ" />
                </Col>
                <Col>
                  <TextWithHint
                    text={values?.contract?.spe.business_name}
                    hint="Razão Social"
                  />
                </Col>
              </Row>
            }
          />
          <DetailsSection
            title="Dados da Incorporadora"
            content={
              <Row className="pt-2">
                <Col xs={2}>
                  <TextWithHint
                    text={values?.contract?.company.cnpj}
                    hint="CNPJ"
                  />
                </Col>
                <Col>
                  <TextWithHint
                    text={values?.contract?.company.business_name}
                    hint="Razão Social"
                  />
                </Col>
              </Row>
            }
          />
          {renderUauIntegrationSection()}
          {values?.contract?.source === 'UAU' &&
            (values?.contract?.buyers?.length || 0) > 1 &&
            can.update('contracts') && (
              <DetailsSection
                title="Responsável Financeiro"
                content={
                  <Row className="pt-2 align-items-center">
                    <Col xs={4}>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={values?.contract?.billing_person_id || ''}
                        onChange={async (e) => {
                          const newPersonId = e.target.value
                          try {
                            await updateBillingPerson(
                              id,
                              newPersonId,
                              user?.token || '',
                              user?.selectedContextEncoded
                            )
                            setValues({
                              ...values,
                              contract: {
                                ...values.contract,
                                billing_person_id: newPersonId
                              }
                            })
                            addToast('Responsável financeiro atualizado', {
                              appearance: 'success',
                              autoDismiss: true
                            })
                          } catch (err: any) {
                            addToast(
                              err.response?.data?.message ||
                                'Erro ao atualizar responsável',
                              { appearance: 'error', autoDismiss: true }
                            )
                          }
                        }}
                      >
                        <option value="">Selecione o responsável...</option>
                        {values?.contract?.buyers?.map((buyer) => (
                          <option key={buyer.id} value={buyer.id}>
                            {(buyer.info as any)?.name ||
                              (buyer.info as any)?.business_name ||
                              buyer.id}
                          </option>
                        ))}
                      </Form.Control>
                    </Col>
                    <Col>
                      <small className="text-muted">
                        Pessoa que aparecerá como pagador nos boletos
                      </small>
                    </Col>
                  </Row>
                }
              />
            )}
          <DetailButtonsRow
            buttons={
              <>
                {values?.contract?.source === 'UAU' &&
                  can.create('billings') && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowItauModal(true)}
                      className="mr-2"
                    >
                      <i className="feather icon-file-text mr-1" />
                      Gerar Boletos Itaú
                    </Button>
                  )}
                {values?.contract?.source === 'UAU' &&
                  can.update('contracts') && (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => setShowSyncInstallmentsModal(true)}
                      className="mr-2"
                      title="Sincroniza parcelas com UAU (detecta renegociações)"
                    >
                      <i className="feather icon-refresh-cw mr-1" />
                      Sincronizar Parcelas UAU
                    </Button>
                  )}
                {can.delete('contracts') &&
                  values?.contract?.status !== ContractStatuses.CANCELLED && (
                    <IconButton
                      onClick={handleOpenDeleteModal}
                      icon="icon-trash"
                      color="#E62355"
                    />
                  )}
                {can.update('contracts') && (
                  <IconButton
                    onClick={() =>
                      history.push('/contracts/create', {
                        id: id,
                        contractParams: values
                      })
                    }
                    icon="icon-edit"
                    color="#5E6EA7"
                  />
                )}
              </>
            }
          />
        </>
      }
      content={
        <>
          <PaymentsTable value={serializedValuesForPaymentsTable(values)} />
          <PersonsTable
            value={serializedValuesForPersonsTable(values)}
            onResendEmail={handleResendEmail}
          />
          <GenerateItauBoletosModal
            show={showItauModal}
            contractId={id}
            onClose={() => setShowItauModal(false)}
            onGenerated={() => {
              setShowItauModal(false)
            }}
          />
          <SyncUauInstallmentsModal
            show={showSyncInstallmentsModal}
            contractId={id}
            onClose={() => setShowSyncInstallmentsModal(false)}
            onApplied={() => {
              // Refresh contract data after sync
              if (typeof window !== 'undefined') window.location.reload()
            }}
          />
        </>
      }
    />
  )
}
